import { Injectable } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { ErrorMessageResponse } from '@app/core/models/data-response';
import { DataTaskService } from '@app/core/services/data-task.service';
import { OrderDialogDialogComponent } from '@app/modal/order-dialog-dialog/order-dialog-dialog.component';
import { firstValueFrom } from 'rxjs';
import { DateRangeInterval } from '@app/core/components/date-range/date-range.component';

@Injectable({
    providedIn: 'root',
})
export class OrderheadService {
    private cancellationCauses?: OrderlineCancellationCauseIiem[];

    constructor(
        public datatask: DataTaskService,
        public dialog: MatDialog,
    ) {}

    /**
     * @datatask 617
     * @datataskName sp_ws_p2_orderhead_get
     * @param params params for order to retrieve.
     * @returns promise containing the orderhead.
     */
    public async getOrderHead(params: GetOrderHeadParams): Promise<OrderHeadGet> {
        return (await this.datatask.Post(617, params))[0];
    }

    /**
     * @datatask 642
     * @datataskName sp_ws_p2_orderhead_save
     * @param params the orderhead to save.
     * @return promise containing validation.
     */
    public async saveOrderHead(params: OrderHeadGet): Promise<ErrorMessageResponse> {
        return (await this.datatask.Post(642, params))[0];
    }

    /**
     * @datatask 890
     * @param params the orderhead to convert from offer to order.
     * @return promise containing validation.
     */
    public async convertOrderHeadToOrder(params: OrderHeadGet): Promise<ErrorMessageResponse> {
        return (await this.datatask.Post(890, params))[0];
    }

    /**
     * Creates or scans a consignment for the order.
     *
     * @datatask 321
     * @param params barcode parameters used by the consignment scan.
     * @returns promise containing the state to navigate to.
     */
    public async scanConsignment(params: ScanConsignmentParams): Promise<ScanConsignmentResponse> {
        return (await this.datatask.Post(321, params))[0];
    }

    /**
     * Creates a Svea payment link for the order.
     *
     * @datatask 2962
     * @param params order identifier.
     * @returns promise containing validation.
     */
    public async createSveaPaymentLink(params: OrderHeadInternalNoParams): Promise<ErrorMessageResponse> {
        return (await this.datatask.Post(2962, params))[0];
    }

    /**
     * Pushes the order to purchase order.
     *
     * @datatask 1366
     * @param params order identifier.
     * @returns promise containing validation.
     */
    public async pushOrderToPurchaseOrder(params: OrderHeadInternalNoParams): Promise<ErrorMessageResponse> {
        return (await this.datatask.Post(1366, params))[0];
    }

    /**
     * Cancels the order head.
     *
     * @datatask 3321
     * @param params order identifier and cancellation details.
     * @returns promise containing validation.
     */
    public async cancelOrderHead(params: CancelOrderHeadParams): Promise<ErrorMessageResponse> {
        return (await this.datatask.Post(3321, params))[0];
    }

    /**
     * Deletes the order head.
     *
     * @datatask 747
     * @param params order identifier.
     * @returns promise containing validation.
     */
    public async deleteOrderHead(params: OrderHeadInternalNoParams): Promise<ErrorMessageResponse> {
        return (await this.datatask.Post(747, params))[0];
    }

    /**
     * @datatask 2965
     * @datataskName tt_delivery_order
     * @param params the orderhead to deliver.
     * @return promise resolving if deliver went through or throws error when fails.
     */
    public async deliverOrder(params: OrderHeadGet): Promise<unknown> {
        return this.datatask.Post(2965, params);
    }

    /**
     * @datatask 2177
     * @datataskName sp_ws_p2_w_orderhead_deliverymethod_changed
     * @param params
     * @returns promise containing info related to the delivery method.
     */
    public async getOrderHeadDeliveryMethodData(params: GetOrderHeadDeliveryMethodDataParams): Promise<OrderHeadDeliveryMethodDataResponse> {
        return (await this.datatask.Post(2177, params))[0];
    }

    /**
     * Retrieves the current order status name.
     *
     * @datatask 3719
     */
    public async getOrderStatusName(params: OrderHeadInternalNoParams): Promise<OrderHeadStatusNameResponse> {
        return (await this.datatask.Post(3719, params))[0];
    }

    /**
     * Retrieves list of cancellation causes for order lines.
     *
     * @datatask 3328
     * @datataskName tt_orderlinecancelcause_list
     * @param params for retreving list.
     * @returns promise containing list of all possible cancellation causes.
     */
    public async getOrderlineCancellationCauses(params?: Partial<GetOrderlineCancellationCausesParams>): Promise<OrderlineCancellationCauseIiem[]> {
        if (!this.cancellationCauses) {
            this.cancellationCauses = await this.datatask.Post(3328, params);
        }

        return this.cancellationCauses!;
    }

    /**
     * Cancels the given orderlines.
     *
     * @datatask 3325
     * @datataskName sp_ws_p2_orderline_cancel_multiple
     * @param params the lines to cancel with reason.
     * @returns promise containing errorcode and message.
     */
    public async cancelOrderlines(params: CancelOrderlinesParams): Promise<ErrorMessageResponse> {
        return (await this.datatask.Post(3325, params))[0];
    }

    /**
     * Retrieves order lines for the legacy list view.
     *
     * @datatask 523
     * @datataskName sp_ws_p2_orderline_list
     * @param params order and view identifier.
     */
    public async getOrderLineList(params: GetOrderLineListParams): Promise<OrderLineLegacyItem[]> {
        return this.datatask.Post(523, params);
    }

    /**
     * Changes the provide method for an order line from the legacy list view.
     *
     * @datatask 1138
     */
    public async changeOrderLineProvideMethod(params: ChangeOrderLineProvideMethodParams): Promise<ErrorMessageResponse> {
        return (await this.datatask.Post(1138, params))[0];
    }

    /**
     * Generates BOM/error-check items for an order line.
     *
     * @datatask 2309
     */
    public async generateOrderLineItems(params: GenerateOrderLineItemsParams): Promise<ErrorMessageResponse> {
        return (await this.datatask.Post(2309, params))[0];
    }

    /**
     * Updates order line numbering after legacy list sorting.
     *
     * @datatask 2917
     */
    public async updateOrderLineNo(params: UpdateOrderLineNoParams): Promise<ErrorMessageResponse> {
        return (await this.datatask.Post(2917, params))[0];
    }

    /**
     * Retrieves lock status before opening a new order line.
     *
     * @datatask 1299
     */
    public async getOrderHeadLockStatus(params: OrderHeadInternalNoParams): Promise<OrderHeadLockStatusResponse> {
        return (await this.datatask.Post(1299, params))[0];
    }

    /**
     * Adds products from price list to the order.
     *
     * @datatask 3736
     */
    public async addProductsFromPricelist(params: OrderHeadGet): Promise<ErrorMessageResponse> {
        return (await this.datatask.Post(3736, params))[0];
    }

    /**
     * Removes zero-quantity order lines from the order.
     *
     * @datatask 3737
     */
    public async removeZeroQuantityLines(params: OrderHeadGet): Promise<ErrorMessageResponse> {
        return (await this.datatask.Post(3737, params))[0];
    }

    /**
     * Updates order prices.
     *
     * @datatask 3814
     */
    public async updateOrderPrices(params: OrderHeadGet): Promise<ErrorMessageResponse> {
        return (await this.datatask.Post(3814, params))[0];
    }

    /**
     * Retrieves order line calculation data.
     *
     * @datatask 3184
     */
    public async getOrderLineCalculation(params: GetOrderLineCalculationParams): Promise<OrderLineCalculation> {
        return (await this.datatask.Post(3184, params, { suppressErrorMessage: true }))[0];
    }

    /**
     * Opens the dialog to add a new comment to the order-dialog.
     *
     * @param params object containing to the order-internal-no to retrieve order-dialog of.
     * @returns promise which resolves when the modal has been closed.
     */
    public async openOrderDialogDialog(params: OrderDialogParams) {
        const dialogRef = this.dialog.open(OrderDialogDialogComponent, { data: params });

        return firstValueFrom(dialogRef.afterClosed());
    }

    /**
     * Retrieves the dialog in a order based on the given params.
     *
     * @param params object containing to the order-internal-no to retrieve order-dialog of.
     * @datatask 1281
     * @datataskName sp_ws_p2_w_orderdialog_list
     * @returns promise containing list of comments in the order-dialog.
     */
    public getOrderDialog(params: OrderDialogParams): Promise<OrderDialogItem[]> {
        return this.datatask.Post(1281, params);
    }

    /**
     * Retrieves the fields for the dynamic-special tab of the orderhead.
     *
     * @param params params used to retrieve the dynamic-special fields.
     * @datatask 3140
     * @datataskName tt_orderhead_dynamic_special
     * @returns a promise resolving in the dynamic special fields.
     */
    public async getDyanmicSpecialOrderHead(params: OrderHeadDynamicSpecialParams): Promise<OrderHeadDynamicSpecialGet> {
        return (await this.datatask.Post(3140, params))[0];
    }

    /**
     * Handles dynamic special tab of orderhead function.
     *
     * @param params the orderhead and function type.
     * @datatask 3152
     * @datataskName tt_orderhead_dynamic_special_functions
     * @returns promise resolving when call has completed.
     */
    public callDynamicSpecialFunctions(params: DynamicSpecialFunctionParams): Promise<unknown> {
        return this.datatask.Post(3152, params);
    }

    /**
     * Retrieves list of sellers.
     *
     * @datatask 628
     * @datataskName sp_ws_p2_seller_list
     * @param params settings for the list.
     * @returns promise containing list of sellers.
     */
    public getSellers(params?: GetSellersParams): Promise<SellerItem[]> {
        return this.datatask.Post(628, params);
    }

    /**
     * Retrieves portal user list for orderhead.
     *
     * @datatask 1046
     * @datataskName sp_ws_p2_portal_user_list_orderhead
     * @param params settings for the list.
     * @returns promise containing list of portal users.
     */
    public getPortalUsers(params?: GetPortalUserParams): Promise<PortalUserItem[]> {
        return this.datatask.Post(1046, params);
    }

    /**
     * Retrieves the available order types.
     *
     * @datatask 612
     * @datataskName sp_ws_p2_ordertype_list
     * @returns promise containing the order types.
     */
    public getOrderTypes(): Promise<OrderTypeItem[]> {
        return this.datatask.Post(612);
    }

    /**
     * Retrieves the available order methods.
     *
     * @datatask 621
     * @datataskName sp_ws_p2_ordermethod_list
     * @returns promise containing the order methods.
     */
    public getOrderMethods(): Promise<OrderMethodItem[]> {
        return this.datatask.Post(621);
    }

    /**
     * Retrieves page get for orderheads page.
     *
     * @datatask 526
     * @datataskName sp_ws_p2_w_orderheads_setting
     * @param params for retrieving page get.
     * @returns promise containing page get.
     */
    public async getOrderHeadsPage(params: GetOrderHeadsPageParams): Promise<OrderHeadsGet> {
        return (await this.datatask.Post(526, params))[0];
    }

    /**
     *
     * @param params
     * @returns
     */
    public getOrderHeadsPaged(params: GetOrderHeadsPagedParams): Promise<OrderHeadsPagedResponse> {
        return this.datatask.Post(1685, params);
    }

    /**
     * Retrieves page get for order-head mi.
     *
     * @datatask 740
     * @datataskName sp_ws_p2_w_orderheadmi_get
     * @param params for retrieving page get.
     * @returns promise containing page get.
     */
    public async getOrderHeadMi(params: GetOrderHeadMiParams): Promise<OrderHeadMiGet> {
        return (await this.datatask.Post(740, params))[0];
    }

    /**
     * Creates an invoice with the given order head invoice settings.
     *
     * @datatask 741
     * @datataskName sp_ws_p2_w_orderheadmi_create_invoice
     * @param params the settings to create invoice with.
     * @returns promise containing validation and the new invoice no if validation went through.
     */
    public async createInvoice(params: OrderHeadMiGet): Promise<{ invoice_no: string; errorcode: string; errormessage: string }> {
        return (await this.datatask.Post(741, params))[0];
    }

    /**
     * Creates an invoice with the given order head invoice settings.
     *
     * @datatask 2918
     * @datataskName sp_ws_p2_w_orderheadmi_create_invoice_manual
     * @param params the settings to create invoice with.
     * @returns promise containing validation and the new invoice no if validation went through.
     */
    public async createManualInvoice(params: CreateManualInvoiceParams): Promise<{ invoice_no: string; errorcode: string; errormessage: string }> {
        return (await this.datatask.Post(2918, params))[0];
    }

    /**
     * @datatask 3629
     * @datataskName sp_ws_p2_w_orderhead_pos_grid_finish_po
     * @param params pos row to set as finished.
     * @returns promise containing validation.
     */
    public async setProductionOrderAsFinished(params: PosGridRow): Promise<ErrorMessageResponse> {
        return (await this.datatask.Post(3629, params))[0];
    }

    /**
     * @datatask 3632
     * @datataskName sp_ws_p2_w_orderhead_delivery_get
     * @param params the order internal no to get delivery for.
     * @returns promise containing the consignment keyno.
     */
    public async getOrderHeadDelivery(params: GetOrderHeadDeliveryParams): Promise<OrderHeadDeliveryGet> {
        return (await this.datatask.Post(3632, params))[0];
    }

    /**
     * @datatask 3633
     * @datataskName sp_ws_p2_w_orderhead_delivery_consignment_list
     * @param params the order internal no to find consignments of.
     * @returns promise containing list of consignments related to the given order.
     */
    public async getConsignments(params: GetOrderHeadConsignmentsParams): Promise<OrderHeadConsignmentItem[]> {
        return this.datatask.Post(3633, params);
    }
}

export interface GetOrderHeadConsignmentsParams {
    order_internal_no: string | number;
}

export interface OrderHeadInternalNoParams {
    order_internal_no: string | number;
}

export interface ScanConsignmentParams {
    barcode: string;
}

export interface ScanConsignmentResponse {
    webpagename: string;
    parms: string | number | Record<string | symbol, unknown> | null;
    errorcode?: string;
    errormessage?: string;
}

export interface CancelOrderHeadParams extends OrderHeadInternalNoParams {
    orderlinecancelcause_keyno: string | number;
    cancellation_note: string;
}

export interface OrderHeadConsignmentItem {
    item_id: string;
    item_name: string;
    orderby: string;
}

export interface GetOrderHeadDeliveryParams {
    order_internal_no: string;
}

export interface OrderHeadDeliveryGet {
    order_internal_no: string;
    consignment_keyno: string;
}

export interface PosGridRow {
    is_selected: boolean;
    po_keyno: number;
    poh_keyno: number;
    poh_keyno_top: number;
    prod_id: string;
    prod_name_long: string;
    quantity_need: number;
    quantity_to_produce: number;
    quantity_finished: number;
    quantity_remaining: number;
    quantity_finish_now: number;
    postatus_no: number;
    postatus_name: string;
    stock_stockvalue: number;
    item_state: string;
    item_parms: string;
    item_path: string;
    item_glyphicon: string;
    item_glyphicon_color: string;
    operations: string;
    planned_date: string;
    stock_keyno: number;
    orderline_prod_id_and_name: string;
}

export interface GetOrderHeadDeliveryMethodDataParams {
    deliverymethod_no: string | number;
}

export interface OrderHeadDeliveryMethodDataResponse {
    pickjobgrp_keyno: string;
}

export interface OrderHeadStatusNameResponse {
    orderstatus_name: string;
}

export interface CreateManualInvoiceParams extends OrderHeadMiGet {
    records: { orderline_keyno: string | number; invoice_quantity_now: string | number }[];
}

export interface GetOrderHeadMiParams {
    order_internal_no: string | number;
}

export interface OrderHeadMiGet {
    order_internal_no: string;
    invoice_date: string;
    invoice_valuta_id: string;
    show_setting: '1' | '0';
    setting_manual_invoicing: '1' | '0';
    setting_goto_invoicehead: '1' | '0';
}

export interface GetOrderHeadsPagedParams {
    page: number;
    pageSize: number;
    argtype: string;
    argvalue: string;
    cust_no: string | number;
    gb_orderheads_related: string;
    gb_orderstatus: string;
    orderstatus_id: string;
    gb_ordertype: string;
    gb_orderdate: string;
    filterval: string;
    ordertype_id: string;
    webpage: string;
    businessco_no: string | number;
    show_all_orders: '1' | '0';
    ordersearch: string;
    gb_regby: string;
    reg_by_portal_user_keyno: string | number;
    date_fom: string;
    date_tom: string;
    show_dateselector: '1' | '0';
}

export interface OrderHeadsPagedResponse {
    items: OrderHeadItem[];
    total: number;
}

export interface OrderHeadItem {
    item_id: number;
    item_name: string;
    item_state: string;
    item_path: string;
    item_parms: number;
    item_glyphicon: string;
    item_glyphicon_color: string;
    orderby: number;
    badgeinfo: string;
    item_name_sub1: string;
    item_filtervalue: string;
}

export interface GetOrderHeadsPageParams {
    argtype: string;
    argvalue: string;
    webpage: string;
}

export interface OrderHeadsGet {
    cust_no: string;
    searchtext_customer: string;
    orderstatus_id: string;
    filterval: string;
    cust_name: string;
    gb_ordertype: string;
    gb_orderdate: string;
    gb_orderstatus: string;
    seller_id: string;
    ordertype_id: string;
    cust_no_and_name: string;
    businessco_no: string;
    show_setting: '1' | '0';
    argtype: string;
    argvalue: string;
    show_all_orders: '1' | '0';
    ordersearch: string;
    show_new_order_button: '1' | '0';
    page_heading: string;
    gb_regby: string;
    show_gb_regby: '1' | '0';
    show_seller_id: '1' | '0';
    reg_by_portal_user_keyno: string;
    date_fom: string;
    date_tom: string;
    dateselector_index: DateRangeInterval;
    show_selection_ordertype: '1' | '0';
    show_dateselector: '1' | '0';
    date_fomtom: string;
    selected_order_internal_no: string;
    search_confirmed_deliverydate: '1' | '0';
    current_portal_user_keyno: string;
    webpage_name: string;
    gb_orderheads_related: string;
}

export interface GetPortalUserParams {
    add_all?: '1' | '0';
    businessco_no?: string | number;
}

export interface PortalUserItem {
    item_id: string;
    item_name: string;
    orderby: string;
}

export interface OrderTypeItem {
    ordertype_id: string;
    ordertype_name: string;
    orderby: string;
}

export interface OrderMethodItem {
    ordermethod_id: string;
    ordermethod_name: string;
    orderby: string;
}

export interface GetSellersParams {
    add_all?: '1' | '0';
    businessco_no?: string | number;
}

export interface SellerItem {
    item_id: string;
    item_name: string;
    item_state: string;
    item_path: string;
    item_parms: string;
    orderby: string;
    seller_no: string;
    seller_name: string;
}

export interface CancelOrderlinesParams {
    orderlines: (number | `${number}`)[];

    orderlinecancelcause_keyno: string;
    cancellation_note: string;
}

export interface GetOrderLineListParams extends OrderHeadInternalNoParams {
    view_id: string;
}

export interface OrderLineLegacyItem {
    item_id: string | number;
    item_name: string;
    item_type?: string;
    item_is_selected?: '1' | '0';
    item_glyphicon?: string;
    item_glyphicon_color?: string;
    item_glyphicon_2?: string;
    item_glyphicon_2_color?: string;
    item_secondary: Array<{
        item_col: string;
        item_col_width?: string | number;
        item_editable?: string;
        visible?: boolean;
        alignment?: string;
        priority?: number;
        orderby?: number;
    }>;
    item_col_width?: string | number;
    orderby?: number;
    provide_method_id?: string | number;
}

export interface ChangeOrderLineProvideMethodParams {
    orderline_keyno: string | number;
    provide_method_id: string | number;
}

export interface GenerateOrderLineItemsParams {
    orderline_keyno: string | number;
}

export interface UpdateOrderLineNoParams {
    orderline_keyno: string | number;
    orderline_no: string | number;
}

export interface OrderHeadLockStatusResponse {
    lockstatusid: string;
    lockstatusinfo: string;
}

export interface GetOrderLineCalculationParams {
    orderline_keyno: string | number;
}

export interface OrderLineCalculation {
    selected_orderline_keyno: string | number | undefined;
    pricelist_id: string | number | undefined;
    pricelist_id2: string | number | undefined;
    purchase_pricelist_id: string | number | undefined;
    purchase_pricelist_id2: string | number | undefined;
    stockplace_id: string | number | undefined;
    price_ordval?: string | number | undefined;
    amount1_ordval?: string | number | undefined;
    amount_ordval_pst?: string | number | undefined;
    amount_total?: string | number | undefined;
    amount_total_pst?: string | number | undefined;
    cost?: string | number | undefined;
    cost_amount?: string | number | undefined;
    cost_pst?: string | number | undefined;
    cost_total?: string | number | undefined;
    cost_total_pst?: string | number | undefined;
    db?: string | number | undefined;
    db_pst?: string | number | undefined;
    db_total?: string | number | undefined;
    db_total_pst?: string | number | undefined;
    special_function?: DynamicSpecialFunctionType | '';
    [key: string]: string | number | undefined;
}

export interface OrderDialogParams {
    order_internal_no: string;
}

export interface OrderDialogItem {
    registered_by_name: string;
    messagetext: string;
    reg_datetime: string;
    orderby: number;
    mymessage: '1' | '0';
}

export interface OrderHeadDynamicSpecialParams {
    order_internal_no: string;
}

export interface GetOrderlineCancellationCausesParams {
    translated?: '1' | '0';
}

export interface OrderlineCancellationCauseIiem {
    item_name: string;
    item_state: string;
    item_parms: string;
    item_path: string;
    orderby: string;
    item_glyphicon: string;
    item_glyphicon_color: string;
}
export type DynamicSpecialFunctionType = 'special_orderhead_construction_save' | 'special_orderhead_construction_orderconfirm' | 'special_orderhead_construction_picklist' | 'special_f_vartdal_db2' | 'special_update_orderprices';

export type DynamicSpecialFunctionParams = (Partial<OrderHeadDynamicSpecialGet> | Partial<OrderHeadGet> | OrderLineCalculation) & { special_function: DynamicSpecialFunctionType | '' };

export interface GetOrderHeadParams {
    order_internal_no: string;
}

export interface OrderHeadGet {
    order_internal_no: string;
    order_order_no: string;
    order_offer_no: string;
    edi_orderhead_keyno: string;
    orderhead_heading: string;
    original_order_internal_no: string;
    special_discount2_keyno: string;
    show_oh_head_special_discount2: '1' | '0';
    stat_cust_no: string | number;
    stat_cust_name: string;
    stat_cust_address: string;
    customers_orderno: string;
    customers_ref1: string;
    customers_ref2: string;
    message_from_sales: string;
    message_to_supplier: string;
    stat_business_id: string;
    stat_coop_id: string;
    stat_search: string;
    stat_search_active: '1' | '0';
    contact_pers_no: string;
    project_keyno: string;
    project_name: string;
    serviceobject_keyno: string;
    serviceobject_name: string;
    show_serviceobject_keyno: '1' | '0';
    svea_payment_uri: string;
    collection_keyno: string;
    cust_note: string;
    locked: '1' | '0';
    show_button_convert_to_order: '1' | '0';

    delivery_cust_no: string;
    delivery_cust_name: string;
    delivery_cust_address: string;
    label: string;
    delivery_email: string;
    deladronpurch: '1' | '0';
    delivery_adr_note: string;
    delivery_business_id: string;
    delivery_coop_id: string;

    delivery_cust_unreg: '1' | '0';
    delivery_adr_street: string;
    delivery_postplace_id_street: string;
    delivery_postplace_name_street: string;
    delivery_adr_mail: string;
    delivery_postplace_id: string;
    delivery_postplace_name: string;
    delivery_cust_phone: string;
    delivery_country_id: string;
    delivery_postplace_id_and_name: string;
    delivery_postplace_id_and_name_street: string;

    invoice_cust_no: string;
    invoice_cust_name: string;
    invoice_cust_address: string;
    invoice_business_id: string;
    invoice_coop_id: string;
    invoice_country_id: string;

    ordermethod_id: string;
    stockplace_id: string;
    seller_no: string;
    businessco_no: string;
    purch_internal_no_from: string;
    orderstatus_name: string;
    order_date: string;
    ordertype_id: string;
    ordertype_name: string;
    consernorder_internal_no: string;
    consernorder_order_no: string;
    data_changed_in_get: '1' | '0';
    reg_by_portal_user_keyno: string;
    department_id: string;
    orderhead_note1: string;
    orderhead_note2: string;
    orderhead_note3: string;
    deliverydate_wish: string;
    deliverydate_text: string;
    deliverydate_confirmed: string;
    deliverytime_confirmed: string;
    printed_orderconfirm: '1' | '0';
    deliverymethod_no: string;
    pickjobgrp_keyno: string;
    show_pickjobgrp_keyno: '1' | '0';
    delivery_place: string;
    deliveryterm_no: string;
    orderpriority_keyno: string;
    freight_paid_by: '1' | '0';
    freight_cost_ordval: string | number;
    freight_cost_ordval_agreed: string | number;
    payment_id: string;
    factoring_no: string;
    pricelist_id: string;
    pricelist_id2: string;
    purchase_pricelist_id: string;
    purchase_pricelist_id2: string;
    tax_keyno: string;
    invoicemethod_no: string;
    verified: '1' | '0';
    order_valuta_id: string;
    invoice_valuta_id: string;
    offer_valid_to_date: string;
    offer_sent: '1' | '0';
    custbonusdeal: string;

    partly_delivery_allowed: '1' | '0'; //98
    show_create_purchhead_interbranch: '1' | '0';
    show_add_orderline: '1' | '0';
    show_consignment_create: '1' | '0';
    show_consignments_goto: '1' | '0';
    show_invoice_make: '1' | '0';
    show_setting: '1' | '0';
    consignmentassembly_webpage_name: string;
    consignmentassembly_parms: string;
    consignmentassembly_custact_no: string;
    show_postcalc: '1' | '0';
    nbr_of_documents: string;
    nbr_of_prepayments: string;
    nbr_of_invoices: string;
    show_orderpurch: '1' | '0';
    show_button_forms: '1' | '0';
    nbr_of_checklistdata: string;
    show_button_timesheetdiary: '1' | '0';
    show_button_setnewdiscount: '1' | '0';
    show_button_deleteorderlines: '1' | '0';
    show_depraceted_buttons: '1' | '0';
    show_button_ohddispatches: '1' | '0';
    show_button_duplicate_order: '1' | '0';
    show_stocktransallocation: '1' | '0';
    show_orderhead_capjobs: '1' | '0';
    show_ordershipmentstatus: '1' | '0';
    show_original_order_internal_no: '1' | '0';
    show_svea_prepayment: '1' | '0';
    show_createcase: '1' | '0';
    show_paymentplans: '1' | '0';
    show_orderlinecredits: '1' | '0';
    nbr_of_custacts: string;
    show_btn_consignment: '1' | '0';
    show_btn_custact: '1' | '0';
    show_btn_pre_payment: '1' | '0';
    show_btn_invoices: '1' | '0';
    show_btn_print: '1' | '0';
    show_btn_email: '1' | '0';
    show_btn_external_cost: '1' | '0';
    show_jobcalc: '1' | '0';
    nbr_of_jobcalcs: string;
    show_positions: '1' | '0';
    nbr_of_positions: string;
    show_gb_orderline_displaytype: '1' | '0';
    nbr_of_deviations: string;
    show_button_deviation: '1' | '0';
    show_button_orderdialog: '1' | '0';
    nbr_of_orderdialogs: string;
    show_button_documents: '1' | '0';
    show_button_pictures: '1' | '0';
    nbr_of_pictures: string;
    show_puch_order_purchaseorder: '1' | '0';
    amount_total_topay: string | number;
    amount_payed: string | number;
    iscashpayment: '1' | '0';
    show_orderline_textile: '1' | '0';
    show_button_pickjobstatusconf_confirm: '1' | '0';
    show_button_pickjobstatusconf_regretconfirm: '1' | '0';
    show_button_pickjobstatusconf_pickjobgenerated: '1' | '0';
    show_direct_order_delivery: '1' | '0';
    gb_row_1: string;
    show_customer_service: '1' | '0';
    show_button_errorcheck_and_generate_bom: '1' | '0';
    show_button_add_orderline_from_pricelist: '1' | '0';
    show_button_remove_zero_qty_orderlines: '1' | '0';
    show_btn_orderhead_price_update: '1' | '0';
    show_buttons_below_grid: '1' | '0';
    show_tab_dynamicpostcalc: '1' | '0';
    show_tab_matcert: '1' | '0';

    show_oh_head_stat_cust_no: '1' | '0';
    show_oh_head_stat_cust_name: '1' | '0';
    show_oh_head_stat_cust_address: '1' | '0';
    show_oh_head_contact_pers_no: '1' | '0';
    show_oh_head_customers_orderno: '1' | '0';
    show_oh_head_project_name: '1' | '0';
    show_oh_head_customers_ref1: '1' | '0';
    show_oh_head_customers_ref2: '1' | '0';
    show_oh_head_message_from_sales: '1' | '0';
    show_oh_head_message_to_supplier: '1' | '0';
    show_oh_head_stat_business_id: '1' | '0';
    show_oh_head_stat_coop_id: '1' | '0';

    show_oh_head_delivery_cust_unreg: '1' | '0';
    show_oh_head_delivery_cust_no: '1' | '0';
    show_oh_head_delivery_cust_name: '1' | '0';
    show_oh_head_delivery_cust_address: '1' | '0';
    show_oh_head_delivery_adr_street: '1' | '0';
    show_oh_head_delivery_post_and_name_street: '1' | '0';
    show_oh_head_delivery_adr_mail: '1' | '0';
    show_oh_head_delivery_post_and_name: '1' | '0';
    show_oh_head_delivery_country_id: '1' | '0';
    show_oh_head_delivery_place: '1' | '0';
    show_oh_head_label: '1' | '0';
    show_oh_head_delivery_cust_phone: '1' | '0';
    show_oh_head_delivery_email: '1' | '0';
    show_oh_head_deladronpurch: '1' | '0';
    show_oh_head_delivery_adr_note: '1' | '0';
    show_oh_head_delivery_business_id: '1' | '0';
    show_oh_head_delivery_coop_id: '1' | '0';

    show_oh_head_invoice_cust_no: '1' | '0';
    show_oh_head_invoice_cust_name: '1' | '0';
    show_oh_head_invoice_cust_address: '1' | '0';
    show_oh_head_invoice_country_id: '1' | '0';
    show_oh_head_invoice_business_id: '1' | '0';
    show_oh_head_invoice_coop_id: '1' | '0';

    show_oh_head_businessco_no: '1' | '0';
    show_oh_head_reg_by_portal_user_keyno: '1' | '0';
    show_oh_head_ordertype_id: '1' | '0';
    show_oh_head_order_date: '1' | '0';
    show_oh_head_order_order_no: '1' | '0';
    show_oh_head_orderstatus_name: '1' | '0';
    show_oh_head_ordermethod_id: '1' | '0';
    show_oh_head_stockplace_id: '1' | '0';
    show_oh_head_seller_no: '1' | '0';
    show_oh_head_department_id: '1' | '0';
    show_oh_head_orderhead_note1: '1' | '0';
    show_oh_head_orderhead_note2: '1' | '0';
    show_oh_head_orderhead_note3: '1' | '0';

    show_oh_head_deliverydate_wish: '1' | '0';
    show_oh_head_deliverydate_text: '1' | '0';
    show_oh_head_deliverydate_confirmed: '1' | '0';
    show_oh_head_deliverytime_confirmed: '1' | '0';
    show_oh_head_deliverymethod: '1' | '0';

    show_oh_head_partly_delivery_allowed: '1' | '0'; //212

    show_oh_head_deliveryterm_no: '1' | '0';
    show_oh_head_freight_paid_by: '1' | '0';
    show_oh_head_freight_cost_ordval: '1' | '0';
    show_oh_head_freight_cost_ordval_agreed: '1' | '0';
    show_oh_head_custbonusdeal: '1' | '0';
    show_oh_head_payment_id: '1' | '0';
    show_oh_head_factoring_no: '1' | '0';
    show_oh_head_pricelist_id: '1' | '0';
    show_oh_head_pricelist_id2: '1' | '0';
    show_oh_head_purchase_pricelist_id: '1' | '0';
    show_oh_head_purchase_pricelist_id2: '1' | '0';
    show_oh_head_tax_keyno: '1' | '0';
    show_oh_head_invoicemethod_no: '1' | '0';
    show_oh_head_order_valuta_id: '1' | '0';
    show_oh_head_invoice_valuta_id: '1' | '0';
    show_oh_head_collection: '1' | '0';

    setting_orderlines_use_grid: '1' | '0';
    setting_orderlines_use_new_grid: '1' | '0';
    setting_orderlines_use_dynamic_grid: '1' | '0';
    setting_oh_show_dynamic_special: '1' | '0';
    setting_orderhead_use_contact_person_info_on_delivery: '1' | '0';
    setting_confirm_convert_to_order: '1' | '0';
    setting_show_orderline_calculation: '1' | '0';
    setting_enable_oh_delete: '1' | '0';
    setting_enable_oh_cancel: '1' | '0';
    carriers_paid_by_customer: '1' | '0';
    orderconfirm_reportdef_keyno: string;
    delivered: '1' | '0';
    show_vertical_purchasing: '1' | '0';
    show_orderform: '1' | '0';
    show_tab_production: '1' | '0';
    show_tab_delivery: '1' | '0';
    show_tab_invoicing: '1' | '0';
    show_tab_hours: '1' | '0';
    gb_orderhead_hours_sub: string;
    show_tab_emails: '1' | '0';
    show_tab_custactac: '1' | '0';
    gb_orderhead_main_production_sub: string;
    show_tab_orderpicks: '1' | '0';
    gb_orderhead_main_orderpicks_status: string;
    show_orderheadchoices: '1' | '0';

    setting_show_oh_head_custbonusdeal: '1' | '0';
    setting_show_oh_head_freight_cost_ordval_agreed: '1' | '0';
    setting_show_oh_head_partly_delivery_allowed: '1' | '0';
    setting_show_oh_head_stat_cust_no: '1' | '0';
    setting_show_oh_head_stat_cust_name: '1' | '0';
    setting_show_oh_head_contact_pers_no: '1' | '0';
    setting_show_oh_head_customers_orderno: '1' | '0';
    setting_show_oh_head_project_name: '1' | '0';
    setting_show_oh_head_customers_ref1: '1' | '0';
    setting_show_oh_head_customers_ref2: '1' | '0';
    setting_show_oh_head_message_from_sales: '1' | '0';
    setting_show_oh_head_message_to_supplier: '1' | '0';
    setting_show_oh_head_stat_cust_address: '1' | '0';
    setting_show_oh_head_stat_coop_id: '1' | '0';
    setting_show_oh_head_delivery_cust_unreg: '1' | '0';
    setting_show_oh_head_delivery_cust_no: '1' | '0';
    setting_show_oh_head_delivery_cust_name: '1' | '0';
    setting_show_oh_head_delivery_cust_address: '1' | '0';
    setting_show_oh_head_delivery_adr_street: '1' | '0';
    setting_show_oh_head_stat_business_id: '1' | '0';
    setting_show_oh_head_delivery_post_and_name: '1' | '0';
    setting_show_oh_head_delivery_country_id: '1' | '0';
    setting_show_oh_head_delivery_place: '1' | '0';
    setting_show_oh_head_label: '1' | '0';
    setting_show_oh_head_delivery_cust_phone: '1' | '0';
    setting_show_oh_head_delivery_email: '1' | '0';
    setting_show_oh_head_delivery_post_and_name_street: '1' | '0';
    setting_show_oh_head_delivery_adr_mail: '1' | '0';
    setting_show_oh_head_invoice_coop_id: '1' | '0';
    setting_show_oh_head_delivery_adr_note: '1' | '0';
    setting_show_oh_head_delivery_business_id: '1' | '0';
    setting_show_oh_head_delivery_coop_id: '1' | '0';
    setting_show_oh_head_invoice_cust_no: '1' | '0';
    setting_show_oh_head_invoice_cust_name: '1' | '0';
    setting_show_oh_head_invoice_cust_address: '1' | '0';
    setting_show_oh_head_deladronpurch: '1' | '0';
    setting_show_oh_head_businessco_no: '1' | '0';
    setting_show_oh_head_reg_by_portal_user_keyno: '1' | '0';
    setting_show_oh_head_ordertype_id: '1' | '0';
    setting_show_oh_head_order_date: '1' | '0';
    setting_show_oh_head_verified: '1' | '0';
    setting_show_oh_head_order_order_no: '1' | '0';
    setting_show_oh_head_order_offer_no: '1' | '0';
    setting_show_oh_head_purch_internal_no_from: '1' | '0';
    setting_show_oh_head_consernorder_order_no: '1' | '0';
    setting_show_oh_head_invoice_country_id: '1' | '0';
    setting_show_oh_head_invoice_business_id: '1' | '0';
    setting_show_oh_head_stockplace_id: '1' | '0';
    setting_show_oh_head_seller_no: '1' | '0';
    setting_show_oh_head_department_id: '1' | '0';
    setting_show_oh_head_offer_valid_to_date: '1' | '0';
    setting_show_oh_head_offer_sent: '1' | '0';
    setting_show_oh_head_deliverydate_wish: '1' | '0';
    setting_show_oh_head_deliverydate_text: '1' | '0';
    setting_show_oh_head_orderstatus_name: '1' | '0';
    setting_show_oh_head_ordermethod_id: '1' | '0';
    setting_show_oh_head_freight_paid_by: '1' | '0';
    setting_show_oh_head_freight_cost_ordval: '1' | '0';
    setting_show_oh_head_payment_id: '1' | '0';
    setting_show_oh_head_factoring_no: '1' | '0';
    setting_show_oh_head_pricelist_id: '1' | '0';
    setting_show_oh_head_deliverydate_confirmed: '1' | '0';
    setting_show_oh_head_printed_orderconfirm: '1' | '0';
    setting_show_oh_head_deliverymethod: '1' | '0';
    setting_show_oh_head_deliveryterm_no: '1' | '0';
    setting_show_oh_head_carriers_paid_by_customer: '1' | '0';
    setting_show_oh_head_tax_keyno: '1' | '0';
    setting_show_oh_head_invoicemethod_no: '1' | '0';
    setting_show_oh_head_order_valuta_id: '1' | '0';
    setting_show_oh_head_invoice_valuta_id: '1' | '0';
    setting_show_oh_head_pricelist_id2: '1' | '0';
    setting_show_oh_head_purchase_pricelist_id: '1' | '0';
    setting_show_oh_head_purchase_pricelist_id2: '1' | '0';

    show_warning_when_converting_to_order_if_customer_has_creditstop: '1' | '0';
}

export interface OrderHeadDynamicSpecialGet {
    // order_internal_no: string;
    // order_order_no: string;
    // order_offer_no: string;
    // orderhead_heading: string;

    // Statistikk-kunden
    // stat_cust_no: string;
    // stat_cust_name: string;
    // stat_cust_address: string;
    // customers_orderno: string;
    // customers_ref1: string;
    // customers_ref2: string;
    // message_from_sales: string;
    // message_to_supplier: string;
    // stat_business_id: string;
    // stat_coop_id: string;
    // stat_search: string;
    // stat_search_active: '1' | '0';
    // contact_pers_no: string;
    // project_keyno: string;
    // project_name: string;
    // serviceobject_keyno: string;
    // serviceobject_name: string;
    // svea_payment_uri: string;

    // // Leveransekunden
    // delivery_cust_no: string;
    // delivery_cust_name: string;
    // delivery_cust_address: string;
    // label: string;
    // delivery_email: string;
    // deladronpurch: '1' | '0';
    // delivery_adr_note: string;
    // delivery_business_id: string;
    // delivery_coop_id: string;

    // // Uregistrert adresse
    // delivery_cust_unreg: '1' | '0';
    // delivery_adr_street: string;
    // delivery_postplace_id_street: string;
    // delivery_postplace_name_street: string;
    // delivery_adr_mail: string;
    // delivery_postplace_id: string;
    // delivery_postplace_name: string;
    // delivery_cust_phone: string;
    // delivery_country_id: string;
    // delivery_postplace_id_and_name: string;
    // delivery_postplace_id_and_name_street: string;

    // // Fakturakunden
    // invoice_cust_no: string;
    // invoice_cust_name: string;
    // invoice_cust_address: string;
    // invoice_business_id: string;
    // invoice_coop_id: string;
    // invoice_country_id: string;

    // // Administrativt
    // ordermethod_id: string;
    // stockplace_id: string;
    // seller_no: string;
    // businessco_no: string;
    // purch_internal_no_from: string;
    // orderstatus_name: string;
    // order_date: string;
    // ordertype_id: string;
    // ordertype_name: string;
    // consernorder_internal_no: string;
    // consernorder_order_no: string;
    // data_changed_in_get: '1' | '0';
    // reg_by_portal_user_keyno: string;
    // department_id: string;

    // // Betingelser
    // deliverydate_wish: string;
    // deliverydate_text: string;
    // deliverydate_confirmed: string;
    // deliverytime_confirmed: string;
    // printed_orderconfirm: '1' | '0';
    // deliverymethod_no: string;
    // pickjobgrp_keyno: string;
    // delivery_place: string;
    // deliveryterm_no: string;
    // orderpriority_keyno: string;
    // freight_paid_by: '1' | '0';
    // freight_cost_ordval: string | number;
    // freight_cost_ordval_agreed: string | number;
    // payment_id: string;
    // factoring_no: string;
    // pricelist_id: string;
    // pricelist_id2: string;
    // purchase_pricelist_id: string;
    // purchase_pricelist_id2: string;
    // tax_keyno: string;
    // invoicemethod_no: string;
    // order_valuta_id: string;
    // invoice_valuta_id: string;
    // offer_valid_to_date: string;

    // // Funksjoner og variabler
    // consignmentassembly_webpage_name: string;
    // consignmentassembly_parms: string;
    // consignmentassembly_custact_no: string;
    // nbr_of_documents: string;
    // nbr_of_prepayments: string;
    // nbr_of_invoices: string;
    // nbr_of_checklistdata: string;

    // // Ordrelinje
    // nbr_of_jobcalcs: string;
    // nbr_of_positions: string;
    // nbr_of_deviations: string;
    // nbr_of_orderdialogs: string;
    // nbr_of_pictures: string;
    // amount_total_topay: string | number;
    // amount_payed: string | number;
    // iscashpayment: '1' | '0';
    // gb_row_1: string;

    // // Innstillingsvariabler for alle felter på Hode-nivå

    // carriers_paid_by_customer: '1' | '0';
    // orderconfirm_reportdef_keyno: string;
    // delivered: '1' | '0';
    edi_description: string;
    // spesial for ordre/bygg
    delivery_cust_phone2: string;
    special_discount2_keyno: string;
    production_date: string;
    tranportation_date: string;
    production_date_confirmed: '1' | '0';
    tranportation_date_confirmed: '1' | '0';
    locked: '1' | '0';
    flexible_delivery_date: '1' | '0';
    cust_contact_phone: string;
    unmanned_contructionsite: '1' | '0';
    message_to_driver: string;
    contactperson: string;
    deliver_to_customer_stock: '1' | '0';
    customer_self_pickup: '1' | '0';
    special_construction01: '1' | '0';
    special_construction02: '1' | '0';
    special_construction03: '1' | '0';
    special_construction04: '1' | '0';
    special_construction05: '1' | '0';
    dlv_time_from: string;
    dlv_time_to: string;
    special_construction06: '1' | '0';
    special_construction07: '1' | '0';
    contact_pers_no2: string;

    registered_date: string;
    registered_time: string;
    regbyusername: string;

    modified_date: string;
    modified_time: string;
    modifiedbyusername: string;

    // Fields added which do not exists anywhere.
    internal_note: string;

    amount_total: string;
    amount_total_pst: string;
    cost_total: string;
    cost_total_pst: string;
    margin: string;
    margin_pst: string;
}
